import React, { memo, useCallback } from "react";

import { Button } from "antd";
import { useIntl } from "react-intl";

import { Company, EntitySupportingCard } from "@mapmycustomers/shared/types/entity";
import { primaryEntityComparator } from "@mapmycustomers/shared/util/comparator";

import noCompaniesImage from "@app/assets/list/preview/no-companies.svg";
import NoData from "@app/component/preview/components/NoData";
import { RELATIONSHIP_DATA_LOAD_LIMIT } from "@app/component/preview/util/consts";

import RelationshipEntityCard from "../RelationshipEntityCard";

interface Props {
  companies: Company[];
  noDataTitle: string;
  onFetchMoreCompanies?: () => void;
  onRemove?: (company: Company) => void;
  parentCompaniesFiltered: Company[];
  primaryCompanyId?: Company["id"];
  totalCompanies: number;
}

const EditRelatedCompanies: React.FC<Props> = ({
  companies,
  noDataTitle,
  onFetchMoreCompanies,
  onRemove,
  parentCompaniesFiltered,
  primaryCompanyId,
  totalCompanies,
}) => {
  const intl = useIntl();
  const offset =
    totalCompanies - companies.length > RELATIONSHIP_DATA_LOAD_LIMIT
      ? RELATIONSHIP_DATA_LOAD_LIMIT
      : totalCompanies - companies.length;

  const showLoadMoreButton = offset > 0 && parentCompaniesFiltered.length === companies.length;

  const handleRemove = useCallback(
    (entity: EntitySupportingCard) => {
      onRemove?.(entity as Company);
    },
    [onRemove]
  );

  const handleLoadMoreCompanies = useCallback(() => {
    onFetchMoreCompanies?.();
  }, [onFetchMoreCompanies]);

  if (parentCompaniesFiltered.length > 0) {
    return (
      <>
        {parentCompaniesFiltered.sort(primaryEntityComparator(primaryCompanyId)).map((company) => (
          <RelationshipEntityCard
            entity={company}
            key={company.id}
            onRemove={handleRemove}
            primary={company.id === primaryCompanyId}
          />
        ))}
        {showLoadMoreButton ? (
          <Button onClick={handleLoadMoreCompanies} type="text">
            {intl.formatMessage(
              {
                id: "associations.companies.loadMore",
                defaultMessage: "Load {count} more",
                description: "Load more companies button",
              },
              { count: offset }
            )}
          </Button>
        ) : null}
      </>
    );
  }

  return <NoData imageSrc={noCompaniesImage} text={noDataTitle} />;
};

export default memo(EditRelatedCompanies);
